// src/native/auth/deepLinkHandler.js
//
// WS-2 AUTH-NATIVE. Receives the OAuth return deep link and establishes the
// session.
//
// Why this file has to exist at all: the web path relies on Supabase's
// detectSessionInUrl (src/lib/supabase.js:17), which reads window.location when
// the page loads at /auth/callback. AuthCallback.jsx then polls getSession every
// 400ms until it appears. A custom scheme deep link never touches
// window.location, the webview is never navigated, and no page load happens, so
// detectSessionInUrl can never see it and the poll would run for its full ten
// seconds and give up. The exchange has to be explicit. exchangeCodeForSession
// appears nowhere else in src/ for exactly that reason.
//
// Why it works: the PKCE code verifier was written to localStorage under
// 'ttt-supabase-auth' by the same webview that is now receiving the deep link,
// because the OS delivers com.timohelps.app:// URLs into this process. Same
// origin, same storage, verifier present. This is precisely the property that
// an email link opened in Safari does not have, and it is why section 4.2 of the
// build spec drops magic links on native rather than trying to rescue them with
// Universal Links.
//
// Inert on web: every entry point returns early on !isNative(), and @capacitor/app
// and @capacitor/browser are only reached through loadNative().

import posthog from 'posthog-js';
import { isNative, loadNative } from '@/lib/platform';
import { supabase } from '@/lib/supabase';
import { isNativeAuthCallbackUrl } from './nativeAuthConfig';
import { nativeAuthError } from './nativeAuthError';

/**
 * Window event dispatched after every terminal outcome of a native auth deep
 * link. detail is { status: 'success' | 'error' | 'cancelled', error?: Error }.
 * Nothing is required to listen; it exists so a native UI layer can react
 * without WS-2 having to edit a file it does not own.
 */
export const NATIVE_AUTH_EVENT = 'ttt:native-auth';

let listenerHandle = null;
let installPromise = null;
let inFlight = false;
let lastHandledUrl = '';

function emit(status, extra = {}) {
  try {
    window.dispatchEvent(new CustomEvent(NATIVE_AUTH_EVENT, { detail: { status, ...extra } }));
  } catch {}
}

/** Dismiss SFSafariViewController. Failure here is cosmetic, never fatal. */
async function closeNativeBrowser() {
  try {
    const mod = await loadNative(() => import('@capacitor/browser'));
    await mod?.Browser?.close();
  } catch {}
}

/** Read the fragment of a deep link as query style params. */
function hashParams(url) {
  try {
    return new URLSearchParams(String(url.hash || '').replace(/^#/, ''));
  } catch {
    return new URLSearchParams();
  }
}

/**
 * Handle one deep link.
 *
 * @param {string} rawUrl
 * @returns {Promise<{ handled: boolean, reason?: string, error?: Error|null }>}
 *
 * `handled: false` means the URL was not ours, so any other listener is free to
 * take it. Never throws.
 */
export async function handleNativeAuthDeepLink(rawUrl) {
  if (!isNative()) return { handled: false, reason: 'not-native' };
  if (!isNativeAuthCallbackUrl(rawUrl)) return { handled: false, reason: 'not-auth-callback' };

  // Cold start delivers the same URL twice: once through getLaunchUrl() and once
  // through appUrlOpen. Exchanging a PKCE code twice fails the second time and
  // would surface as a spurious error, so collapse the duplicate.
  if (inFlight) return { handled: false, reason: 'in-flight' };
  if (rawUrl === lastHandledUrl) return { handled: false, reason: 'duplicate' };

  let url;
  try {
    url = new URL(rawUrl);
  } catch {
    return { handled: false, reason: 'unparseable' };
  }

  inFlight = true;
  lastHandledUrl = rawUrl;

  try {
    const fragment = hashParams(url);

    // The provider or Supabase can refuse. Both put the reason in the query on
    // the PKCE flow; the fragment is checked too so an implicit flow response is
    // not silently swallowed.
    const oauthError = url.searchParams.get('error') || fragment.get('error');
    if (oauthError) {
      const description =
        url.searchParams.get('error_description') || fragment.get('error_description') || '';
      const cancelled = /access_denied|user_cancelled|cancell?ed/i.test(`${oauthError} ${description}`);
      await closeNativeBrowser();
      try {
        posthog.capture('native_auth_deeplink_failed', {
          reason: cancelled ? 'cancelled' : 'provider_error',
          code: oauthError,
        });
      } catch {}
      // A cancellation is a normal thing a user does, so let them try again with
      // the identical URL rather than treating it as already handled.
      lastHandledUrl = '';
      const err = nativeAuthError(
        `OAuth returned ${oauthError}${description ? `: ${description}` : ''}`,
        cancelled ? 'native.auth.oauthCancelled' : 'native.auth.oauthFailed'
      );
      emit(cancelled ? 'cancelled' : 'error', { error: err });
      return { handled: true, reason: cancelled ? 'cancelled' : 'provider-error', error: err };
    }

    // The PKCE happy path.
    const code = url.searchParams.get('code');
    if (code) {
      const { data, error } = await supabase.auth.exchangeCodeForSession(code);
      await closeNativeBrowser();
      if (error) {
        lastHandledUrl = '';
        try {
          posthog.capture('native_auth_deeplink_failed', {
            reason: 'exchange_failed',
            message: error.message,
          });
        } catch {}
        const err = nativeAuthError(
          error.message || 'exchangeCodeForSession failed',
          'native.auth.sessionFailed',
          error
        );
        emit('error', { error: err });
        return { handled: true, reason: 'exchange-failed', error: err };
      }
      try {
        posthog.capture('native_auth_deeplink_success', { method: 'pkce' });
      } catch {}
      // onAuthStateChange now fires inside AuthContext, which sets the user and
      // flips subscriptionLoading. The existing redirect guard in Login.jsx then
      // routes to /home, /onboarding or /pricing exactly as it does on the web.
      emit('success', { session: !!data?.session });
      return { handled: true, reason: 'exchanged', error: null };
    }

    // Defensive fallback. If the Supabase project or a specific provider ever
    // answers with an implicit grant, the tokens arrive in the fragment and
    // there is no code to exchange. setSession accepts them directly and fires
    // the same onAuthStateChange.
    const accessToken = fragment.get('access_token');
    const refreshToken = fragment.get('refresh_token');
    if (accessToken && refreshToken) {
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      await closeNativeBrowser();
      if (error) {
        lastHandledUrl = '';
        try {
          posthog.capture('native_auth_deeplink_failed', {
            reason: 'set_session_failed',
            message: error.message,
          });
        } catch {}
        const err = nativeAuthError(
          error.message || 'setSession failed',
          'native.auth.sessionFailed',
          error
        );
        emit('error', { error: err });
        return { handled: true, reason: 'set-session-failed', error: err };
      }
      try {
        posthog.capture('native_auth_deeplink_success', { method: 'implicit' });
      } catch {}
      emit('success', { session: true });
      return { handled: true, reason: 'session-set', error: null };
    }

    // Our host and path, but nothing usable in it.
    await closeNativeBrowser();
    lastHandledUrl = '';
    try {
      posthog.capture('native_auth_deeplink_failed', { reason: 'no_credentials' });
    } catch {}
    const err = nativeAuthError('Auth deep link carried no code and no tokens', 'native.auth.sessionFailed');
    emit('error', { error: err });
    return { handled: true, reason: 'no-credentials', error: err };
  } catch (caught) {
    lastHandledUrl = '';
    try {
      posthog.capture('native_auth_deeplink_failed', { reason: 'exception', message: String(caught) });
    } catch {}
    const err = nativeAuthError('Native auth deep link threw', 'native.auth.sessionFailed', caught);
    emit('error', { error: err });
    return { handled: true, reason: 'exception', error: err };
  } finally {
    inFlight = false;
  }
}

/**
 * Install the appUrlOpen listener. Safe to call more than once; only the first
 * call does anything. Call it as early as the React tree allows, because a cold
 * start triggered by the deep link itself is a real case.
 *
 * @returns {Promise<{ installed: boolean, reason?: string }>}
 */
export async function installDeepLinkHandler() {
  if (!isNative()) return { installed: false, reason: 'not-native' };
  if (listenerHandle) return { installed: true, reason: 'already-installed' };
  if (installPromise) return installPromise;

  installPromise = (async () => {
    const mod = await loadNative(() => import('@capacitor/app'));
    const CapApp = mod?.App;
    if (!CapApp) {
      return { installed: false, reason: 'plugin-unavailable' };
    }

    try {
      listenerHandle = await CapApp.addListener('appUrlOpen', (event) => {
        void handleNativeAuthDeepLink(event?.url);
      });
    } catch (err) {
      try {
        posthog.capture('native_deeplink_listener_failed', { message: String(err) });
      } catch {}
      return { installed: false, reason: 'listener-failed' };
    }

    // If the OS launched the app cold with the URL, appUrlOpen may already have
    // fired before this listener existed. getLaunchUrl replays it. The duplicate
    // guard above makes the overlap harmless.
    try {
      const launch = await CapApp.getLaunchUrl();
      if (launch?.url) void handleNativeAuthDeepLink(launch.url);
    } catch {}

    try {
      posthog.capture('native_deeplink_listener_installed');
    } catch {}
    return { installed: true };
  })();

  const result = await installPromise;
  installPromise = null;
  return result;
}

/** Tear the listener down. Only useful in tests and hot reload. */
export async function removeDeepLinkHandler() {
  const handle = listenerHandle;
  listenerHandle = null;
  installPromise = null;
  lastHandledUrl = '';
  inFlight = false;
  try {
    await handle?.remove?.();
  } catch {}
}
